import { IonLabel } from '@ionic/react';
import { ReactElement } from 'react';
import { IoWarning } from 'react-icons/io5';
import {
  MdNewReleases,
  MdOutlineCancel,
  MdPause,
  MdPlayArrow,
  MdPowerSettingsNew,
  MdRemoveCircle,
  MdReport,
  MdStop,
} from 'react-icons/md';
import './machine.css';
import { Machine } from '../../api/graphql/generated';
import { useMachineConnected } from '../../api/hooks/machine.hooks';

enum SafetyState {
  OFF = 'OFF',
  SAFETY_MODE_ROBOT_EMERGENCY_STOP = 'SAFETY_MODE_ROBOT_EMERGENCY_STOP',
  SAFETY_MODE_SAFEGUARD_STOP = 'SAFETY_MODE_SAFEGUARD_STOP',
  SAFETY_MODE_PROTECTIVE_STOP = 'SAFETY_MODE_PROTECTIVE_STOP',
  SAFETY_MODE_REDUCED = 'SAFETY_MODE_REDUCED',
  SAFETY_MODE_VIOLATION = 'SAFETY_MODE_VIOLATION',
  NOT_SET = 'NOT_SET',
  SAFETY_MODE_FAULT = 'SAFETY_MODE_FAULT',
  SAFETY_MODE_NORMAL = 'SAFETY_MODE_NORMAL',
  SAFETY_MODE_RECOVERY = 'SAFETY_MODE_RECOVERY',
}

enum MachineStateEnum {
  OFF = 'OFF',
  ROBOT_MODE_BOOTING = 'ROBOT_MODE_BOOTING',
  ROBOT_MODE_UPDATING_FIRMWARE = 'ROBOT_MODE_UPDATING_FIRMWARE',
  ROBOT_MODE_POWER_OFF = 'ROBOT_MODE_POWER_OFF',
  ROBOT_MODE_BACKDRIVE = 'ROBOT_MODE_BACKDRIVE',
  ROBOT_MODE_RUNNING = 'ROBOT_MODE_RUNNING',
  ROBOT_MODE_POWER_ON = 'ROBOT_MODE_POWER_ON',
  ROBOT_MODE_CONFIRM_SAFETY = 'ROBOT_MODE_CONFIRM_SAFETY',
  ROBOT_MODE_IDLE = 'ROBOT_MODE_IDLE',
  ROBOT_MODE_DISCONNECTED = 'ROBOT_MODE_DISCONNECTED',
}

enum ProgramState {
  OFF = 'OFF',
  PROGRAM_STOPPED = 'PROGRAM_STOPPED',
  PROGRAM_PAUSED = 'PROGRAM_PAUSED',
  NOT_SET = 'NOT_SET',
  PROGRAM_PAUSING = 'PROGRAM_PAUSING',
  PROGRAM_PLAYING = 'PROGRAM_PLAYING',
  PROGRAM_STOPPING = 'PROGRAM_STOPPING',
}

interface IMachineStateIcon {
  machine: Machine;
}

const MachineStateIcon = (props: IMachineStateIcon): ReactElement => {
  const { machine } = props;
  let iconColor = 'medium';
  let Icon = MdPowerSettingsNew;

  const isMachineActive = machine.active;
  const isConnected = useMachineConnected({ machine });

  const programState: ProgramState | undefined = isMachineActive
    ? machine.state?.transient?.status?.programState
    : undefined;
  const machineState: MachineStateEnum | undefined = isMachineActive
    ? machine.state?.transient?.status?.machineState
    : undefined;
  const safetyState: SafetyState | undefined = isMachineActive
    ? machine.state?.transient?.status?.safetyState
    : undefined;

  if (machineState) {
    const poweredOff = [
      MachineStateEnum.ROBOT_MODE_DISCONNECTED,
      MachineStateEnum.ROBOT_MODE_POWER_OFF,
    ].includes(machineState);

    if (!isConnected && !poweredOff) {
      // red triangle around exclamation point
      iconColor = 'danger';
      Icon = IoWarning;
    } else if (safetyState === SafetyState.SAFETY_MODE_SAFEGUARD_STOP) {
      // yellow jagged circle around exclamation point
      iconColor = 'warning';
      Icon = MdNewReleases;
    } else if (safetyState === SafetyState.SAFETY_MODE_ROBOT_EMERGENCY_STOP) {
      // red circle around a minus sign
      iconColor = 'danger';
      Icon = MdRemoveCircle;
    } else if (safetyState === SafetyState.SAFETY_MODE_PROTECTIVE_STOP) {
      // red octagon around an exclamation point
      iconColor = 'danger';
      Icon = MdReport;
    } else if (poweredOff) {
      // grey power button (circle with a vertical line through top of it)
      iconColor = 'medium';
      Icon = MdPowerSettingsNew;
    } else if (programState === ProgramState.PROGRAM_PLAYING) {
      // green play button (sideways triangle)
      iconColor = 'success';
      Icon = MdPlayArrow;
    } else if (programState === ProgramState.PROGRAM_PAUSED) {
      // yellow pause button (two vertical bars)
      iconColor = 'warning';
      Icon = MdPause;
    } else if (programState === ProgramState.PROGRAM_STOPPED) {
      // red stop button (square)
      iconColor = 'danger';
      Icon = MdStop;
    }
  } else if (!isMachineActive) {
    // red circle around an X
    iconColor = 'danger';
    Icon = MdOutlineCancel;
  }

  return (
    <IonLabel color={`${iconColor}`} style={{ pointerEvents: 'none' }}>
      <Icon className="machine-state-icon" style={{ pointerEvents: 'none' }} />
    </IonLabel>
  );
};

export { MachineStateEnum, MachineStateIcon, ProgramState, SafetyState };
